import type { OrganizationDashboardData, RecentEvent } from '@/components/organization/OrganizationHomeDashboard';

export const ORGANIZATION_DASHBOARD_LOAD_TIMEOUT_MS = 20000;

type AxiosLike = {
  data?: unknown;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readOrganization(value: unknown): OrganizationDashboardData['organization'] | null {
  if (!isRecord(value) || typeof value.id !== 'string' || typeof value.name !== 'string') {
    return null;
  }

  return {
    id: value.id,
    name: value.name,
    type: typeof value.type === 'string' ? value.type : undefined,
    domain: typeof value.domain === 'string' ? value.domain : undefined,
    logo_url: typeof value.logo_url === 'string' ? value.logo_url : value.logo_url === null ? null : undefined,
  };
}

function asNumber(value: unknown, fallback = 0): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/** Accept `{ data: { organization, ... } }` or a bare dashboard object. */
export function parseOrganizationDashboardPayload(res: AxiosLike | unknown): OrganizationDashboardData | null {
  const root = isRecord(res) && 'data' in res ? (res as AxiosLike).data : res;
  const payload = isRecord(root) && isRecord(root.data) && readOrganization(root.data.organization)
    ? root.data
    : isRecord(root) && readOrganization(root.organization)
      ? root
      : null;

  if (!payload) return null;
  const organization = readOrganization(payload.organization);
  if (!organization) return null;

  return {
    organization,
    total_events: asNumber(payload.total_events),
    active_events: asNumber(payload.active_events),
    total_organizers: asNumber(payload.total_organizers),
    total_participants: asNumber(payload.total_participants),
    total_registrations: asNumber(payload.total_registrations ?? payload.total_participants),
    total_revenue: asNumber(payload.total_revenue),
    pending_approvals: asNumber(payload.pending_approvals),
  };
}

export function parseOrganizationRecentEvents(res: AxiosLike | unknown): RecentEvent[] {
  const root = isRecord(res) && 'data' in res ? (res as AxiosLike).data : res;
  const payload = isRecord(root) && 'data' in root ? root.data : root;
  const rows = isRecord(payload) && Array.isArray(payload.data)
    ? payload.data
    : Array.isArray(payload)
      ? payload
      : [];

  return rows.filter((row): row is RecentEvent => isRecord(row) && typeof row.id === 'string' && typeof row.title === 'string');
}

export function organizationDashboardErrorMessage(err: unknown, fallback = 'Failed to load dashboard'): string {
  if (isRecord(err) && isRecord(err.response) && isRecord(err.response.data) && typeof err.response.data.message === 'string') {
    return err.response.data.message;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/** Keep the skeleton only while persist is hydrating or a session check has no user yet. */
export function shouldHoldOrganizationDashboardLoading(options: {
  hydrated: boolean;
  authChecking: boolean;
  hasUser: boolean;
}): boolean {
  if (!options.hydrated) return true;
  return options.authChecking && !options.hasUser;
}
